/**
 * Utility functions for billing address validation
 */

export interface BillingAddressCheckResult {
  hasBillingInfo: boolean;
  billingType: "personal" | "company" | "none";
  isDefaultPersonal: boolean;
  isDefaultCompany: boolean;
}

export interface BillingAddress {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  countryId?: string;
  countryCode?: string;
  companyName?: string;
  taxId?: string;
  vatNumber?: string;
  idType?: string;
  isDefault?: boolean;
  isVatValid?: boolean;
}

/**
 * Check if a billing address has all required fields
 * @param billingInfo - Billing address object
 * @returns boolean indicating if all required fields are present and not empty
 */
export const isCompleteBillingAddress = (billingInfo: BillingAddress | null | undefined): boolean => {
  if (!billingInfo) {
    return false;
  }

  // Check all required fields are present and not empty (trimmed)
  const hasStreet = !!billingInfo.street && billingInfo.street.trim().length > 0;
  const hasCity = !!billingInfo.city && billingInfo.city.trim().length > 0;
  const hasState = !!billingInfo.state && billingInfo.state.trim().length > 0;
  const hasZipCode = !!billingInfo.zipCode && billingInfo.zipCode.trim().length > 0;
  
  // Country can be either country name or countryId - at least one should exist
  const hasCountry = (!!billingInfo.country && billingInfo.country.trim().length > 0) || 
                     (!!billingInfo.countryId && billingInfo.countryId.trim().length > 0);

  // All required fields must be present
  return hasStreet && hasCity && hasState && hasZipCode && hasCountry;
};

/**
 * Check if a company billing address has all required fields
 * Company billing requires the same fields as personal billing
 * @param billingInfo - Company billing address object
 * @returns boolean indicating if all required fields are present
 */
export const isCompleteCompanyBillingAddress = (billingInfo: BillingAddress | null | undefined): boolean => {
  // Company billing has same requirements as personal billing
  // Company name is optional
  return isCompleteBillingAddress(billingInfo);
};

/**
 * Get the default billing address from user
 * @param user - User object from Redux state
 * @returns The default billing address or null
 */
export const getDefaultBillingAddress = (user: any): { address: BillingAddress | null; type: "personal" | "company" | "none" } => {
  if (!user) {
    return { address: null, type: "none" };
  }

  // Check which address is marked as default
  if (user.billingInformation?.isDefault && isCompleteBillingAddress(user.billingInformation)) {
    return { address: user.billingInformation, type: "personal" };
  }

  if (user.companyBillingInformation?.isDefault && isCompleteCompanyBillingAddress(user.companyBillingInformation)) {
    return { address: user.companyBillingInformation, type: "company" };
  }

  // Fallback: if no explicit default is set, check for complete addresses
  // Prefer personal billing if both exist
  if (isCompleteBillingAddress(user.billingInformation)) {
    return { address: user.billingInformation, type: "personal" };
  }

  if (isCompleteCompanyBillingAddress(user.companyBillingInformation)) {
    return { address: user.companyBillingInformation, type: "company" };
  }

  return { address: null, type: "none" };
};

/**
 * Check if user has any complete billing information (personal or company)
 * @param user - User object from Redux state
 * @returns BillingAddressCheckResult with detailed billing information status
 */
export const checkUserBillingInfo = (user: any): BillingAddressCheckResult => {
  if (!user) {
    return { 
      hasBillingInfo: false, 
      billingType: "none",
      isDefaultPersonal: false,
      isDefaultCompany: false
    };
  }

  const hasPersonalBilling = isCompleteBillingAddress(user.billingInformation);
  const hasCompanyBilling = isCompleteCompanyBillingAddress(user.companyBillingInformation);

  // Determine which is default
  const isDefaultPersonal = !!user.billingInformation?.isDefault;
  const isDefaultCompany = !!user.companyBillingInformation?.isDefault;

  // If one is explicitly marked as default and complete, use it
  if (hasPersonalBilling && isDefaultPersonal) {
    return { 
      hasBillingInfo: true, 
      billingType: "personal",
      isDefaultPersonal: true,
      isDefaultCompany: false
    };
  }

  if (hasCompanyBilling && isDefaultCompany) {
    return { 
      hasBillingInfo: true, 
      billingType: "company",
      isDefaultPersonal: false,
      isDefaultCompany: true
    };
  }

  // Fallback: prefer personal if both exist and none is marked as default
  if (hasPersonalBilling) {
    return { 
      hasBillingInfo: true, 
      billingType: "personal",
      isDefaultPersonal: true,
      isDefaultCompany: false
    };
  }

  if (hasCompanyBilling) {
    return { 
      hasBillingInfo: true, 
      billingType: "company",
      isDefaultPersonal: false,
      isDefaultCompany: true
    };
  }

  return { 
    hasBillingInfo: false, 
    billingType: "none",
    isDefaultPersonal: false,
    isDefaultCompany: false
  };
};
